import request from '../api';
import { getRestauranteFromStorage, ensureRestauranteLoaded } from './selectedRestaurante';

/**
 * Converte string de hora "HH:mm" para minutos desde meia-noite
 * @param {string} timeString - Hora no formato "HH:mm"
 * @returns {number} - Minutos desde meia-noite
 */
const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
};

/**
 * Converte número do dia para nome em português
 * @param {number} day - Dia da semana (1-7, onde 1=domingo)
 * @returns {string} - Nome do dia
 */
const getDayName = (day) => {
    const days = {
        1: 'Domingo',
        2: 'Segunda-feira',
        3: 'Terça-feira',
        4: 'Quarta-feira',
        5: 'Quinta-feira',
        6: 'Sexta-feira',
        7: 'Sábado'
    };
    return days[day] || 'Dia desconhecido';
};

/**
 * Calcula o próximo horário de abertura baseado nos horários de funcionamento
 * @param {Array} horariosFuncionamento - Array de horários
 * @returns {string|null} - Próximo horário de abertura formatado
 */
const calcularProximaAbertura = (horariosFuncionamento) => {
    if (!Array.isArray(horariosFuncionamento) || horariosFuncionamento.length === 0) {
        return null;
    }

    const agora = new Date();
    const diaAtual = agora.getDay() + 1; // Converter para formato da API (1=domingo)
    const minutoAtual = agora.getHours() * 60 + agora.getMinutes();

    // Primeiro, verificar se ainda abre hoje
    const horarioHoje = horariosFuncionamento.find(h => h.dia === diaAtual);
    if (horarioHoje) {
        const aberturaHoje = timeToMinutes(horarioHoje.abertura);
        if (minutoAtual < aberturaHoje) {
            return `Hoje às ${horarioHoje.abertura}`;
        }
    }

    // Buscar próximo dia com funcionamento (máximo 7 dias)
    for (let i = 1; i <= 7; i++) {
        const proximoDia = ((diaAtual - 1 + i) % 7) + 1;
        const horarioProximo = horariosFuncionamento.find(h => h.dia === proximoDia);
        
        if (horarioProximo) {
            const nomeDia = getDayName(proximoDia);
            if (i === 1) {
                return `Amanhã às ${horarioProximo.abertura}`;
            } else {
                return `${nomeDia} às ${horarioProximo.abertura}`;
            }
        }
    }

    return null;
};

/**
 * Busca o status do restaurante via API
 * @returns {Promise<Object>} - { isAberto, horariosFuncionamento, proximaAbertura }
 */
export const fetchRestaurantStatus = async () => {
    try {
        let restaurante = getRestauranteFromStorage();
        
        if (!restaurante) {
            restaurante = await ensureRestauranteLoaded();
        }

        if (!restaurante || (!restaurante.id && !restaurante.ID)) {
            console.warn('Restaurante não encontrado no storage, usando fallback seguro');
            // Retornar como aberto para não bloquear o funcionamento
            return { isAberto: true, horariosFuncionamento: [], proximaAbertura: null };
        }

        const restauranteId = restaurante.id || restaurante.ID;
        
        // Usar a API que já calcula se está aberto
        const { data } = await request.get(`/restaurante/get-all`);
        
        // Encontrar o restaurante específico no array
        const restauranteData = Array.isArray(data) ? 
            data.find(r => (r.id || r.ID) === restauranteId) : 
            null;
        
        if (!restauranteData) {
            return { isAberto: true, horariosFuncionamento: [], proximaAbertura: null };
        }
        
        // Calcular próxima abertura se estiver fechado
        let proximaAbertura = null;
        if (!restauranteData.isAberto && restauranteData.horariosFuncionamento) {
            proximaAbertura = calcularProximaAbertura(restauranteData.horariosFuncionamento);
        }

        console.log('Status do restaurante:', {
            isAberto: restauranteData.isAberto,
            horarioAtual: restauranteData.horarioAtual,
            proximaAbertura
        });
        
        return {
            isAberto: restauranteData.isAberto,
            horariosFuncionamento: restauranteData.horariosFuncionamento || [],
            proximaAbertura
        };
    } catch (error) {
        console.error('Erro ao buscar status do restaurante:', error);
        // Em caso de erro, assumir que está aberto para não bloquear o totem
        return { isAberto: true, horariosFuncionamento: [], proximaAbertura: null };
    }
};

// Manter compatibilidade com código existente
export const checkRestaurantStatus = fetchRestaurantStatus;
